import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import useSEO from "@/hooks/useSEO";

const DemoPage = () => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);

  useSEO({
    title: "デモ申し込み | 決済管理システムの無料デモ体験",
    description: "Stream Solutions Limitedの決済管理システムデモを申し込む。30分のオンラインデモで実際の画面をご確認いただけます。入金管理・消込処理・自動化システムの導入効果を体験してください。",
    keywords: "決済管理デモ,システムデモ申込,フィンテックデモ,消込システムデモ,無料デモ",
    canonical: "/demo",
    ogTitle: "デモ申し込み | Stream Solutions Limited",
    ogDescription: "30分のオンラインデモで決済管理システムの導入効果をご確認ください。無料でお申し込みいただけます。",
  });

  const [submitting, setSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitState, setSubmitState] = useState<"idle" | "success" | "error">("idle");
  const [confirmData, setConfirmData] = useState<Record<string, string> | null>(null);
  const [payload, setPayload] = useState<URLSearchParams | null>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!agreed) {
      setError("プライバシーポリシーへの同意が必要です。");
      return;
    }
    setError("");
    setSubmitting(true);

    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    const preview: Record<string, string> = {};
    formData.forEach((value, key) => {
      const text = value.toString();
      data.append(key, text);
      preview[key] = text;
    });
    setPayload(data);
    setConfirmData(preview);
    setShowConfirm(true);
  };

  const handleConfirmedSend = async () => {
    if (!payload) return;

    setShowConfirm(false);
    setSubmitting(true);
    setSubmitState("idle");

    try {
      const res = await fetch("/send.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const text = await res.text();
      if (res.ok && text.trim() === "OK") {
        formRef.current?.reset();
        setAgreed(false);
        setCharCount(0);
        setPayload(null);
        setConfirmData(null);
        setSubmitState("success");
      } else {
        setSubmitState("error");
      }
    } catch {
      setSubmitState("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=futuristic-financial-dashboard-demo-presentation-dark-background-with-glowing-screens-data-visualization-holographic-display-cyan-accent-lights-no-people-clean-minimal-fintech&width=1920&height=600&seq=demohero001&orientation=landscape"
            alt="demo hero"
            className="w-full h-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 to-slate-950" />
        </div>
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: "linear-gradient(rgba(6,182,212,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,0.3) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="w-8 h-px bg-cyan-500" />
            <span className="text-cyan-400 text-xs font-semibold tracking-[0.3em] uppercase">DEMO REQUEST</span>
            <span className="w-8 h-px bg-cyan-500" />
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">デモを申し込む</h1>
          <p className="text-slate-400 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
            実際のシステム画面をご覧いただきながら、導入効果をご確認いただけます。
          </p>
        </div>
      </section>

      {/* Demo features */}
      <section className="py-16 bg-white border-b border-slate-100">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: "ri-time-line", title: "30分のオンラインデモ", desc: "実際のシステム画面を画面共有でご覧いただけます" },
              { icon: "ri-question-answer-line", title: "質疑応答", desc: "専任担当者が疑問点にリアルタイムでお答えします" },
              { icon: "ri-file-list-3-line", title: "カスタム提案", desc: "貴社の業務に合わせた導入プランをご提案します" },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-4 p-5 rounded-2xl bg-slate-50">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-cyan-50 flex-shrink-0">
                  <i className={`${item.icon} text-cyan-600`} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900 mb-1">{item.title}</div>
                  <div className="text-xs text-slate-500 leading-relaxed">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6 lg:px-10">
            <form
              ref={formRef}
              id="demo-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">デモ申し込みフォーム</h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-2">
                    お名前 <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="山田 太郎"
                    className="w-full px-4 py-3 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-cyan-400 focus:bg-white transition-all duration-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-2">
                    会社名 <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="company"
                    required
                    placeholder="株式会社〇〇"
                    className="w-full px-4 py-3 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-cyan-400 focus:bg-white transition-all duration-200"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">
                  メールアドレス <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="example@company.com"
                  className="w-full px-4 py-3 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-cyan-400 focus:bg-white transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">電話番号</label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="03-0000-0000"
                  className="w-full px-4 py-3 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-cyan-400 focus:bg-white transition-all duration-200"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">ご希望の日時（第1希望）</label>
                <input
                  type="datetime-local"
                  name="preferred_date_1"
                  className="w-full px-4 py-3 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-cyan-400 focus:bg-white transition-all duration-200 cursor-pointer"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">ご希望の日時（第2希望）</label>
                <input
                  type="datetime-local"
                  name="preferred_date_2"
                  className="w-full px-4 py-3 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-cyan-400 focus:bg-white transition-all duration-200 cursor-pointer"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">ご要望・ご質問</label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="特にご確認されたい点などがあればご記入ください（500文字以内）"
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-3 text-sm rounded-xl border border-slate-200 bg-slate-50 focus:outline-none focus:border-cyan-400 focus:bg-white transition-all duration-200 resize-none"
                />
                <div className="text-right text-xs text-slate-400 mt-1">{charCount}/500</div>
              </div>

              {/* Privacy checkbox */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <input type="hidden" name="privacy" value={agreed ? "同意する" : ""} />
                <input type="hidden" name="inquiry_type" value="demo" />
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-cyan-500 cursor-pointer"
                  />
                  <span className="text-xs text-slate-600 leading-relaxed">
                    <a href="/privacy" className="text-cyan-600 hover:underline font-semibold" target="_blank" rel="noopener noreferrer">
                      プライバシーポリシー
                    </a>
                    に同意の上、送信します。
                    <span className="text-rose-500 ml-1">*</span>
                  </span>
                </label>
              </div>

              {error && (
                <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-600 text-xs">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !agreed}
                className="w-full py-4 bg-cyan-500 hover:bg-cyan-400 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all duration-300 cursor-pointer whitespace-nowrap text-sm"
              >
                入力内容を確認する
              </button>
            </form>
        </div>
      </section>

      {showConfirm && confirmData && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl p-8 max-h-[85vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">入力内容の確認</h3>
            <p className="text-slate-500 text-sm mb-6">送信前に内容をご確認ください。</p>
            <div className="space-y-4 mb-8">
              {[
                ["お名前", confirmData.name],
                ["会社名", confirmData.company],
                ["メールアドレス", confirmData.email],
                ["電話番号", confirmData.phone || "未入力"],
                ["ご希望の日時（第1希望）", confirmData.preferred_date_1 || "未入力"],
                ["ご希望の日時（第2希望）", confirmData.preferred_date_2 || "未入力"],
                ["ご要望・ご質問", confirmData.message || "未入力"],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs font-semibold text-slate-500 mb-1">{label}</div>
                  <div className="text-sm text-slate-900 whitespace-pre-wrap break-words">{value}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button type="button" onClick={() => setShowConfirm(false)} className="flex-1 py-3 rounded-xl border border-slate-300 text-slate-700 font-semibold hover:bg-slate-100 transition-colors whitespace-nowrap cursor-pointer">
                戻る
              </button>
              <button type="button" onClick={handleConfirmedSend} className="flex-1 py-3 rounded-xl bg-cyan-500 text-white font-semibold hover:bg-cyan-400 transition-colors whitespace-nowrap cursor-pointer">
                この内容で送信する
              </button>
            </div>
          </div>
        </div>
      )}

      {submitting && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 text-center">
            <i className="ri-loader-4-line animate-spin text-4xl text-cyan-500"></i>
            <p className="mt-4 text-slate-900 font-semibold">送信中です...</p>
          </div>
        </div>
      )}

      {submitState !== "idle" && !submitting && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 text-center">
            <i className={`${submitState === "success" ? "ri-checkbox-circle-fill text-green-500" : "ri-error-warning-fill text-rose-500"} text-5xl`}></i>
            <p className="mt-4 text-slate-900 font-semibold">
              {submitState === "success"
                ? "デモ申し込みが完了しました。1〜2営業日以内に担当者よりご連絡いたします。"
                : "送信に失敗しました。しばらくしてから再度お試しください。"}
            </p>
            <button type="button" onClick={() => setSubmitState("idle")} className="mt-6 px-6 py-3 rounded-xl bg-cyan-500 text-white font-semibold hover:bg-cyan-400 transition-colors whitespace-nowrap cursor-pointer">
              閉じる
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default DemoPage;
